import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { categoryService, ProductCategory } from '../../services/configurationService';
import { customToast } from '../../utils/customToast';
import { Plus, Edit2, Trash2, Eye, EyeOff } from 'lucide-react';
import CategoryModal from './CategoryModal';
import ConfirmationModal from '../ConfirmationModal';

const CategoriesManagement: React.FC = () => {
  const { theme } = useTheme();
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<ProductCategory | null>(null);
  const [deletingCategory, setDeletingCategory] = useState<ProductCategory | null>(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const data = await categoryService.getAll();
      setCategories(data);
    } catch (error) {
      customToast.error('Failed to load categories');
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingCategory(null);
    setShowModal(true);
  };

  const handleEdit = (category: ProductCategory) => {
    setEditingCategory(category);
    setShowModal(true);
  };

  const handleDelete = async () => {
    if (!deletingCategory) return;

    try {
      await categoryService.delete(deletingCategory.id);
      customToast.success('Category deleted successfully');
      setDeletingCategory(null);
      fetchCategories();
    } catch (error: any) {
      customToast.error(error.response?.data?.message || 'Failed to delete category');
    }
  };

  const handleSave = async () => {
    setShowModal(false);
    await fetchCategories();
  };

  if (loading) {
    const skeletonColor = theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300';
    return (
      <div className="space-y-4 sm:space-y-6">
        {/* Header Skeleton */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4">
          <div className="flex-1 min-w-0">
            <div className={`h-6 w-40 rounded animate-pulse ${skeletonColor}`} />
            <div className={`h-4 w-56 rounded animate-pulse mt-2 ${skeletonColor}`} />
          </div>
          <div className={`w-full sm:w-auto h-10 rounded-lg animate-pulse ${skeletonColor}`} />
        </div>

        {/* Table Skeleton */}
        <div className={`rounded-lg border ${
          theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className={theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'}>
                <tr>
                  <th className="px-6 py-3">
                    <div className={`h-4 w-20 rounded animate-pulse ${skeletonColor}`} />
                  </th>
                  <th className="px-6 py-3">
                    <div className={`h-4 w-32 rounded animate-pulse ${skeletonColor}`} />
                  </th>
                  <th className="px-6 py-3">
                    <div className={`h-4 w-16 rounded animate-pulse ${skeletonColor}`} />
                  </th>
                  <th className="px-6 py-3 text-right">
                    <div className={`h-4 w-16 rounded animate-pulse ${skeletonColor} ml-auto`} />
                  </th>
                </tr>
              </thead>
              <tbody className={`divide-y ${theme === 'dark' ? 'divide-gray-700' : 'divide-gray-200'}`}>
                {[1, 2, 3, 4, 5].map((idx) => (
                  <tr key={idx} className={theme === 'dark' ? 'bg-gray-800' : 'bg-white'}>
                    {/* Column 1: Category Name */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`h-4 w-32 rounded animate-pulse ${skeletonColor}`} />
                    </td>

                    {/* Column 2: Description */}
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        <div className={`h-4 w-full rounded animate-pulse ${skeletonColor}`} />
                        <div className={`h-3 w-4/5 rounded animate-pulse ${skeletonColor}`} />
                      </div>
                    </td>

                    {/* Column 3: Status Badge (with icon + text) */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`h-6 w-24 rounded-full animate-pulse ${skeletonColor}`} />
                    </td>

                    {/* Column 4: Actions (Edit + Delete buttons with icon + text) */}
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex justify-end space-x-2">
                        {/* Edit Button */}
                        <div className={`h-6 w-16 rounded animate-pulse ${skeletonColor}`} />
                        {/* Delete Button */}
                        <div className={`h-6 w-20 rounded animate-pulse ${skeletonColor}`} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4">
        <div className="flex-1 min-w-0">
          <h2 className={`text-lg sm:text-xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            Product Categories
          </h2>
          <p className={`text-xs sm:text-sm mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            Manage your product categories
          </p>
        </div>
        <button
          onClick={handleAdd}
          className="w-full sm:w-auto px-3 sm:px-4 py-2 sm:py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center justify-center space-x-2 transition-colors text-sm font-medium touch-manipulation"
        >
          <Plus className="h-4 w-4" />
          <span>Add Category</span>
        </button>
      </div>

      {/* Categories Table */}
      <div className={`rounded-lg border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'}>
              <tr>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  Category
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  Description
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  Status
                </th>
                <th className={`px-6 py-3 text-right text-xs font-medium uppercase tracking-wider ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className={`divide-y ${theme === 'dark' ? 'divide-gray-700 bg-gray-800' : 'divide-gray-200 bg-white'}`}>
              {categories.map((category) => (
                <tr key={category.id} className={theme === 'dark' ? 'hover:bg-gray-750' : 'hover:bg-gray-50'}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      {category.name}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      {category.description || '-'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      category.is_active
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                        : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400'
                    }`}>
                      {category.is_active ? (
                        <><Eye className="w-3 h-3 mr-1" /> Active</>
                      ) : (
                        <><EyeOff className="w-3 h-3 mr-1" /> Inactive</>
                      )}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                    <button
                      onClick={() => handleEdit(category)}
                      className={`inline-flex items-center px-3 py-1.5 rounded ${
                        theme === 'dark'
                          ? 'text-blue-400 hover:bg-gray-700'
                          : 'text-blue-600 hover:bg-blue-50'
                      }`}
                    >
                      <Edit2 className="w-4 h-4 mr-1" />
                      Edit
                    </button>
                    <button
                      onClick={() => setDeletingCategory(category)}
                      className={`inline-flex items-center px-3 py-1.5 rounded ${
                        theme === 'dark'
                          ? 'text-red-400 hover:bg-gray-700'
                          : 'text-red-600 hover:bg-red-50'
                      }`}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modals */}
      {showModal && (
        <CategoryModal
          category={editingCategory}
          onClose={() => setShowModal(false)}
          onSave={handleSave}
        />
      )}

      {deletingCategory && (
        <ConfirmationModal
          isOpen={!!deletingCategory}
          onClose={() => setDeletingCategory(null)}
          onConfirm={handleDelete}
          title="Delete Category"
          message={`Are you sure you want to delete "${deletingCategory.name}"? This action cannot be undone.`}
          confirmText="Delete"
          type="danger"
        />
      )}
    </div>
  );
};

export default CategoriesManagement;
